import { useToast } from '@app/core/notifications';
import { SocialPlatformId } from '@app/modules/social-export/domain/platforms';
import { fragmentsDeliveryAPIClientEnableDelivery, socialExport } from '@mi-front/api-clients';
import { GetFragmentDto } from '@mi-front/core';
import { useIntl } from 'react-intl';
import { apiClient } from '../../../api';
import { routeGenerator } from '../routing/FragmentsRoutes';
import { CreateDefaultFragmentData, CreateJointFragmentData, Fragment } from '../types';

interface FragmentCreateOptions {
  onSuccess?: (fragment: GetFragmentDto) => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export function useFragmentCreate(options?: FragmentCreateOptions) {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const socialExportStart = socialExport.useCreateFragmentUpload({
    mutation: {},
  });

  async function save(data: CreateDefaultFragmentData | CreateJointFragmentData) {
    try {
      const { platformUpload, doExternalExport, ...rest } = data;

      const fragment = await apiClient.fragments.create(rest);

      if (doExternalExport) {
        try {
          await fragmentsDeliveryAPIClientEnableDelivery(fragment.id, {});
        } catch {
          notifyError(
            formatMessage({
              id: 'fragments.addScreen.externalExportError',
              defaultMessage: 'No se pudo iniciar el export externo',
            }),
          );
        }
      }

      if (platformUpload) {
        for (const p in platformUpload) {
          const platform = p as SocialPlatformId;
          const data = platformUpload[platform];

          if (data?.enabled) {
            await socialExportStart
              .mutateAsync({
                fragment: fragment.id,
                data: {
                  platform,
                  data: data.config ?? {},
                } as unknown as socialExport.CreateFragmentUploadDto,
              })
              .catch((err: unknown) => {
                console.error(err);
                notifyError(
                  formatMessage({
                    id: 'fragments.addScreen.exportError',
                    defaultMessage: 'No se pudo iniciar el export externo',
                  }),
                );
              });
          }
        }
      }

      if (options?.onSuccess) {
        await options.onSuccess(fragment);
      } else {
        notifySuccess(
          formatMessage({
            id: 'fragments.addScreen.created',
            defaultMessage: 'Fragmento creado',
          }),
          {
            link: {
              text: formatMessage({
                id: 'fragments.addScreen.goTo',
                defaultMessage: 'Ir al fragmento',
              }),
              to: routeGenerator.view(fragment),
            },
          },
        );
      }
    } catch (err: unknown) {
      if (options?.onError) {
        options.onError(err);
      } else {
        notifyError(
          formatMessage({
            id: 'fragments.addScreen.createError',
            defaultMessage: 'No se pudo crear el fragmento',
          }),
        );
      }

      throw err;
    }
  }

  return save;
}

export function useFragmentEdit() {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();

  async function save(data: Pick<Fragment, 'stationId' | 'id' | 'title' | 'description' | 'tags'>) {
    try {
      await apiClient.fragments.edit(data);

      notifySuccess(
        formatMessage({
          id: 'fragments.addScreen.updated',
          defaultMessage: 'Fragmento actualizado',
        }),
      );
    } catch (err: unknown) {
      notifyError(
        formatMessage({
          id: 'fragments.addScreen.updateError',
          defaultMessage: 'No se pudo actualizar el fragmento',
        }),
      );

      throw err;
    }
  }

  return {
    save,
  };
}
